/**
 * Type safe property name
 *
 * see https://schneidenbach.gitbooks.io/typescript-cookbook/content/nameof-operator.html
 * @param name Property name
 * @returns Property name
 */
import { parsePhoneNumber } from 'awesome-phonenumber';
import { isValid, parseISO } from 'date-fns';
import { z } from 'zod';
import { APPLICATION_ID_LENGTH, LABELS } from './constants';
import { amountApplicable, electrify2515HouseholdCash, electrify2515HouseholdLoan, householdSchema } from './shared';
import { PensionType, Period } from './types';

type ValidateExpenseProps = {
  data: z.infer<typeof amountApplicable>;
  ctx: z.RefinementCtx;
  isMonthlyExpense?: boolean;
  requireIsNotApplicable?: boolean;
  allowZeroAmount?: boolean;
};

const { PHONE_NUMBER } = LABELS;

export const toCamelCase = (string: string) => {
  const [head, ...tail] = string
    .split(/-|_/)
    .map(([head, ...tail]) => [head.toUpperCase(), tail.join('').toLowerCase()].join(''))
    .join('');
  return [head.toLowerCase(), ...tail].join('');
};

export const validateDateOfBirth = (value: Date) => {
  const today = new Date();
  const elderThanEighteenYears = new Date();
  const olderOverHundredYears = new Date();
  elderThanEighteenYears.setFullYear(today.getFullYear() - 18);
  olderOverHundredYears.setFullYear(today.getFullYear() - 100);
  return value.getTime() <= elderThanEighteenYears.getTime() && value.getTime() >= olderOverHundredYears.getTime();
};

export const nameof = <T>(name: keyof T) => name;

export const requiredFieldError = (fieldLabel = 'This') => `${fieldLabel} is a required field`;
export const fieldsToValidate = {
  PASSPORT: ['expiryDate', 'number', 'issuingCountry'],
  DRIVING_LICENSE: ['number', 'cardNumber', 'state', 'expiryDate'],
  MEDICARE: ['number', 'reference', 'color', 'expiryDate'],
  employed: ['employerBusinessName', 'employerPhoneNumber', 'jobTitle'],
  'self-employed': ['abn', 'phone'],
  pensioner: ['pensionType'],
  SELF_EMPLOYED: ['abn', 'phone'],
  PHOTO_ID: ['number', 'state'],
};

export const validateField = <T extends z.ZodObject<z.ZodRawShape>>(
  value: z.infer<T>,
  ctx: z.RefinementCtx,
  type: string,
  requiredFields: string[],
  showDefaultLabel = false,
) => {
  type = toCamelCase(type);

  requiredFields.forEach(field => {
    if (!value[`${type}`]?.[`${field}`]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: showDefaultLabel ? `This is a required field` : `${type} ${field} is required`,
        path: [type, field],
      });
    }
  });
};

export const isValidDate = (value: string) => {
  return isValid(parseISO(value));
};

export const validateDriversLicenseCardNumber = (state: string, cardNumber: string, number: string) => {
  if (cardNumber === number) {
    return {
      isValid: false,
      error: 'Card number cannot be the same as license number',
    };
  }

  if (cardNumber) {
    let isValid: boolean;
    switch (state) {
      case 'ACT':
      case 'QLD':
        isValid = /^[a-zA-Z\d]{10}$/.test(cardNumber);
        break;
      case 'NSW':
        isValid = /^\d{8,10}$/.test(cardNumber);
        break;
      case 'NT':
        isValid = /^\d{6,8}$/.test(cardNumber);
        break;
      case 'SA':
      case 'TAS':
        isValid = /^[a-zA-Z\d]{9}$/.test(cardNumber);
        break;
      case 'WA':
        isValid = /^[a-zA-Z\d]{8,10}$/.test(cardNumber);
        break;
      case 'VIC':
        isValid = /^[a-zA-Z\d]{8}$/.test(cardNumber);
        break;
      default:
        isValid = true;
        break;
    }

    return {
      isValid,
      error: isValid ? '' : "Enter a valid driver's licence card number",
    };
  }

  return {
    isValid: true,
    error: '',
  };
};

export const requiredNumberString = (fieldName?: string) =>
  z
    .string({
      required_error: requiredFieldError(fieldName),
    })
    .min(1, requiredFieldError(fieldName))
    .transform(val => val.replace(/\s+/g, ''));

export const numberString = () =>
  z
    .string()
    .transform(val => val.replace(/\s+/g, ''))
    .optional();

export const isValidFileExtension = (filename: string, extensions: string[]) => {
  return extensions.includes(filename.split('.').pop()?.toLowerCase() || '');
};

export const requiredString = (fieldName?: string) =>
  z
    .string({ required_error: requiredFieldError(fieldName) })
    .min(1, requiredFieldError(fieldName))
    .trim();

// truncate filename to 255 chars
export const truncateFileName = (filename: string) => {
  const parts = filename.split('.');
  const extension = parts.pop() || '';
  const name = parts.join('.');
  return `${name.substring(0, 255 - extension.length - 1)}.${extension}`;
};

export const validateExpense = ({
  data,
  ctx,
  isMonthlyExpense = false,
  requireIsNotApplicable = false,
  allowZeroAmount = false,
}: ValidateExpenseProps) => {
  const { isNotApplicable, amount, period } = data;

  if (requireIsNotApplicable && isNotApplicable === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredFieldError(),
      path: ['isNotApplicable'],
    });
    return;
  }

  if (!isNotApplicable) {
    const isAmountInvalid = allowZeroAmount
      ? amount === undefined || amount === null || amount < 0
      : !amount || amount <= 0;
    if (isAmountInvalid) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: allowZeroAmount ? 0 : 1,
        type: 'number',
        inclusive: true,
        message: requiredFieldError(),
        path: ['amount'],
      });
    }
    if (!period && !isMonthlyExpense) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'string',
        inclusive: true,
        message: requiredFieldError(),
        path: ['period'],
      });
    }
  }
};

export const validateMobile = z
  .string({ required_error: requiredFieldError() })
  .refine(
    data => {
      if (!data) {
        return true;
      }

      if (!/^\d+$/.test(data.replace(/\s+/g, ''))) {
        return false;
      }

      const pn = parsePhoneNumber(data, { regionCode: 'AU' });
      return pn.valid && pn.type === 'mobile' && data.startsWith('0');
    },
    { message: 'Invalid mobile number' },
  )
  .transform(data => data.replace(/\s+/g, ''));

export const validateRequiredMobile = z
  .string({ required_error: requiredFieldError(PHONE_NUMBER) })
  .min(1, requiredFieldError(PHONE_NUMBER))
  .refine(
    data => {
      if (!/^\d+$/.test(data.replace(/\s+/g, ''))) {
        return false;
      }
      const pn = parsePhoneNumber(data, { regionCode: 'AU' });
      return pn.valid && pn.type === 'mobile' && data.startsWith('0');
    },
    { message: 'Invalid mobile number' },
  )
  .transform(data => data.replace(/\s+/g, ''));
export const validateMobileAndPhone = z
  .string()
  .refine(
    data => {
      if (!data) {
        return true;
      }

      if (!/^\d+$/.test(data.replace(/\s+/g, ''))) {
        return false;
      }

      const pn = parsePhoneNumber(data, { regionCode: 'AU' });

      return pn.valid;
    },
    { message: 'Please enter a valid Australian number' },
  )
  .transform(data => data.replace(/\s+/g, ''));

export const requiredApplicationIdString = () =>
  z.string().length(APPLICATION_ID_LENGTH, `Application ID must be ${APPLICATION_ID_LENGTH} characters long`);

type ValidateMonthlyIncomeType = {
  data: z.infer<typeof amountApplicable>;
  ctx: z.RefinementCtx;
  message: string;
  min: number;
  path: string[];
};

export const validateMonthlyIncome = ({ data, ctx, message, min, path }: ValidateMonthlyIncomeType) => {
  const { amount, period } = data;

  if (amount !== undefined) {
    let monthlyIncome = amount;

    switch (period) {
      case Period.Weekly:
        // there are 52 weeks in a year, so divide by 12 to get monthly income
        monthlyIncome = (amount * 52) / 12;
        break;
      case Period.Annually:
        monthlyIncome = amount / 12;
        break;
      case Period.Fortnightly:
        // there are 26 fortnights in a year, so divide by 12 to get monthly income
        monthlyIncome = (amount * 26) / 12;
        break;
    }

    if (monthlyIncome <= min) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path,
      });
    }
  }
};

export const validateE2515HouseholdData = (
  data: z.infer<typeof householdSchema>,
  ctx: z.RefinementCtx,
  isCashApplication: boolean,
) => {
  const schema = isCashApplication ? electrify2515HouseholdCash : electrify2515HouseholdLoan;
  const result = schema.safeParse(data);

  if (!result.success) {
    result.error.issues.forEach(issue => {
      ctx.addIssue(issue);
    });
  }
};

export const validateStringLength = (fieldName: string, characterLength: number) =>
  z
    .string({ required_error: requiredFieldError(fieldName) })
    .min(1, requiredFieldError(fieldName))
    .max(characterLength, `${fieldName} must not exceed ${characterLength} characters`)
    .trim();

export const isPensionerIncomeValidationRequired = (
  pensionerType: PensionType,
  dateOfBirth: Date,
  monthlyIncome: number,
  incomeThreshold: number = 2500,
) => {
  const APPROXIMATE_MILLISECONDS_IN_A_YEAR = 1000 * 60 * 60 * 24 * 365.25; // Average year in milliseconds
  // Retiree always requires income verification
  if (pensionerType === PensionType.SELF_FUNDED_RETIREE) {
    return true;
  }

  const age = Math.floor((Date.now() - dateOfBirth.getTime()) / APPROXIMATE_MILLISECONDS_IN_A_YEAR);

  if (age < 66 || age > 75) {
    return true;
  }

  return monthlyIncome > incomeThreshold;
};

export const convertAmountToAnnualPeriod = (amount: number, period: Period) => {
  switch (period) {
    case Period.Weekly:
      return amount * 52;
    case Period.Fortnightly:
      return amount * 26;
    case Period.Monthly:
      return amount * 12;
    default:
      return amount;
  }
};

export const convertAmountToMonthlyPeriod = (amount: number, period: Period): number => {
  switch (period) {
    case Period.Weekly:
      return (amount * 52) / 12;
    case Period.Fortnightly:
      return (amount * 26) / 12;
    case Period.Annually:
      return amount / 12;
    default:
      return amount;
  }
};
export function pascalToCamelCase(value: string) {
  if (value) {
    return value.charAt(0).toLowerCase() + value.slice(1);
  }
  return value;
}

export function getEnumKey<T, K extends object>(value: unknown, sourceEnum: K) {
  return Object.keys(sourceEnum)[Object.values(sourceEnum).indexOf(value)] as T;
}
